import React, { useRef, useState, useEffect } from "react";
import {
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  LinearProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  FiUploadCloud,
  FiSearch,
  FiMic,
  FiFileText,
  FiCheck,
  FiX,
  FiFolder,
  FiAlertCircle,
} from "react-icons/fi";
import { fileAPI } from "../../api/apiHelper.js";
import FileManager from "./FileManager.jsx";

export default function Uploads() {
  const navigate = useNavigate();
  const audioFileInputRef = useRef();
  const docFileInputRef = useRef();
  const competitorFileInputRef = useRef();
  const [audioDragActive, setAudioDragActive] = useState(false);
  const [docDragActive, setDocDragActive] = useState(false);
  const [competitorDragActive, setCompetitorDragActive] = useState(false);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [selectedCompetitor, setSelectedCompetitor] = useState("competitor1");

  // Upload state management
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Utility functions
  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const validateFile = (file, type) => {
    const maxSize = 50 * 1024 * 1024; // 50MB

    if (file.size > maxSize) {
      showSnackbar(
        `File ${file.name} is too large. Maximum size is 50MB.`,
        "error"
      );
      return false;
    }

    if (type === "audioFile") {
      const allowedTypes = [
        "audio/mpeg",
        "audio/mp3",
        "audio/wav",
        "audio/mp4",
        "audio/aac",
        "audio/ogg",
        "audio/webm",
      ];
      if (!allowedTypes.includes(file.type)) {
        showSnackbar(
          `File ${file.name} is not a supported audio format.`,
          "error"
        );
        return false;
      }
    } else if (type === "companyDocs" || type.startsWith("competitor")) {
      const allowedTypes = [
        "application/pdf",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];
      if (!allowedTypes.includes(file.type)) {
        showSnackbar(
          `File ${file.name} is not a supported document format.`,
          "error"
        );
        return false;
      }
    }

    return true;
  };

  const uploadFile = async (file, type) => {
    if (!validateFile(file, type)) return;

    const fileId = `${Date.now()}_${file.name}`;
    // setUploadingFiles((prev) => [...prev, fileId]);
    setUploadingFiles((prev) => [...prev, { id: fileId, type }]);

    setUploadProgress((prev) => ({ ...prev, [fileId]: 0 }));
    console.log("Uploaded Files", uploadedFiles);

    try {
      // Step 1: Generate upload URL/path
      const uploadData = await fileAPI.generateUploadUrl(
        file.name,
        file.type,
        file.size,
        type
      );

      // Step 2: Upload file based on storage type
      if (uploadData.data.storageType === "gcs") {
        // Upload to Google Cloud Storage
        await fileAPI.uploadToGCS(
          uploadData.data.signedUrl,
          file,
          (progress) => {
            setUploadProgress((prev) => ({ ...prev, [fileId]: progress }));
          }
        );
      } else {
        // Upload to local storage (fallback)
        const localUploadResult = await fileAPI.uploadToBackend(
          file,
          (progress) => {
            setUploadProgress((prev) => ({ ...prev, [fileId]: progress }));
          }
        );

        // For local uploads, the metadata is already saved by the backend
        // No need to call saveFileMetadata separately
        console.log("Local upload completed:", localUploadResult);
      }

      // Step 3: Save file metadata (only for GCS uploads)
      if (uploadData.data.storageType === "gcs") {
        const metadata = {
          fileName: uploadData.data.fileName,
          originalFileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          type: type,
          gcsPath: uploadData.data.gcsPath,
          publicUrl: uploadData.data.publicUrl,
        };

        await fileAPI.saveFileMetadata(metadata);
      }

      // Success
      setUploadedFiles((prev) => [
        ...prev,
        { id: fileId, name: file.name, type },
      ]);

      const storageType =
        uploadData.data.storageType === "gcs" ? "cloud" : "local";
      showSnackbar(
        `File ${file.name} uploaded successfully to ${storageType} storage!`,
        "success"
      );
    } catch (error) {
      console.error("Upload error:", error);
      showSnackbar(`Failed to upload ${file.name}: ${error.message}`, "error");
    } finally {
      // setUploadingFiles((prev) => prev.filter((id) => id !== fileId));
      setUploadingFiles((prev) => prev.filter((f) => f.id !== fileId));

      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[fileId];
        return newProgress;
      });
    }
  };

  const handleAudioDragOver = (e) => {
    e.preventDefault();
    setAudioDragActive(true);
  };
  const handleAudioDragLeave = (e) => {
    e.preventDefault();
    setAudioDragActive(false);
  };
  const handleAudioDrop = (e) => {
    e.preventDefault();
    setAudioDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    files.forEach((file) => uploadFile(file, "audioFile"));
  };
  const handleAudioFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => uploadFile(file, "audioFile"));
    e.target.value = ""; // Reset input
  };

  const handleDocDragOver = (e) => {
    e.preventDefault();
    setDocDragActive(true);
  };
  const handleDocDragLeave = (e) => {
    e.preventDefault();
    setDocDragActive(false);
  };
  const handleDocDrop = (e) => {
    e.preventDefault();
    setDocDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    files.forEach((file) => uploadFile(file, "companyDocs"));
  };
  const handleDocFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => uploadFile(file, "companyDocs"));
    e.target.value = ""; // Reset input
  };

  const handleCompetitorDragOver = (e) => {
    e.preventDefault();
    setCompetitorDragActive(true);
  };
  const handleCompetitorDragLeave = (e) => {
    e.preventDefault();
    setCompetitorDragActive(false);
  };
  const handleCompetitorDrop = (e) => {
    e.preventDefault();
    setCompetitorDragActive(false);
    const files = Array.from(e.dataTransfer.files);
    files.forEach((file) => uploadFile(file, selectedCompetitor));
  };
  const handleCompetitorFileSelect = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => uploadFile(file, selectedCompetitor));
    e.target.value = ""; // Reset input
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Tab panel component
  const TabPanel = ({ children, value, index, ...other }) => {
    return (
      <div
        role="tabpanel"
        hidden={ value !== index }
        id={ `upload-tabpanel-${index}` }
        aria-labelledby={ `upload-tab-${index}` }
        { ...other }
      >
        { value === index && <Box sx={ { py: 1 } }>{ children }</Box> }
      </div>
    );
  };

  return (
    <div
      style={ {
        margin: "0 auto",
        background: "#fff",
        borderRadius: 18,
        minHeight: 480,
      } }
    >
      {/* Maintenance Banner - temporarily hidden
      <Box
        sx={ {
          mb: 4,
          p: 4,
          borderRadius: 4,
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          border: '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 2.5,
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
        } }
      >
        <div style={ {
          background: '#eff6ff',
          padding: '16px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        } }>
          <FiAlertCircle size={ 32 } style={ { color: "#3b82f6" } } />
        </div>

        <Box>
          <Typography variant="h5" fontWeight={ 800 } color="#1e293b" gutterBottom>
            Feature Update & AI Insights
          </Typography>
          <Typography variant="body1" color="#64748b" sx={ { maxWidth: 700, mx: 'auto', fontSize: '1.05rem', lineHeight: 1.6 } }>
            The advanced file upload and automated AI insight features are currently being optimized to provide you with the most accurate analysis.
            This functionality is temporarily restricted.
          </Typography>
          <Typography variant="body2" color="#94a3b8" sx={ { mt: 1, fontWeight: 500 } }>
            Interested in early access? Connect with our specialist team to explore these capabilities.
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={ () => window.open("/contact", "_blank") }
          sx={ {
            mt: 1,
            background: "linear-gradient(to right, #3b82f6, #2563eb)",
            color: "#fff",
            borderRadius: '10px',
            px: 5,
            py: 1.8,
            textTransform: 'none',
            fontSize: '15px',
            fontWeight: 700,
            boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.3)',
            '&:hover': {
              background: "linear-gradient(to right, #2563eb, #1d4ed8)",
              transform: 'translateY(-1px)',
              boxShadow: '0 20px 25px -5px rgba(37, 99, 235, 0.4)'
            },
            transition: 'all 0.2s ease'
          } }
        >
          Contact Us for Full Access
        </Button>
      </Box>
      */}

      {/* Tabs */ }
      <Box sx={ { borderBottom: 1, borderColor: "divider", mb: 1 } }>
        <Tabs value={ activeTab } onChange={ handleTabChange }>
          <Tab
            label="Upload Files"
            icon={ <FiUploadCloud size={ 16 } /> }
            iconPosition="start"
            sx={ { py: 0.5 } }
          />
          <Tab
            label="Manage Files"
            icon={ <FiFolder size={ 16 } /> }
            iconPosition="start"
            sx={ { py: 0.5 } }
          />
        </Tabs>
      </Box>

      {/* Tab Panels */ }
      <TabPanel value={ activeTab } index={ 0 }>
        <Box display="flex" gap={ 3 }>
          {/* Left Side - Audio Upload */ }
          <Box flex={ 1 }>
            <Box display="flex" alignItems="center" gap={ 2 } mb={ 3 }>
              <FiMic size={ 24 } style={ { color: "#396afc" } } />
              <Typography variant="h6" fontWeight={ 700 }>
                Audio Upload
              </Typography>
            </Box>
            <Typography variant="body2" color="#64748b" mb={ 3 } lineHeight={ 1.6 }>
              Upload audio or video files to generate transcripts and insights
              from your meetings, calls, or presentations.
            </Typography>

            <Box
              onDragOver={ handleAudioDragOver }
              onDragLeave={ handleAudioDragLeave }
              onDrop={ handleAudioDrop }
              sx={ {
                border: audioDragActive
                  ? "2px solid #396afc"
                  : "2px dashed #e0e7ef",
                borderRadius: 3,
                background: audioDragActive ? "#f4f5fa" : "#fff",
                minHeight: 200,
                padding: "2rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "border 0.2s, background 0.2s",
                position: "relative",
              } }
              onClick={ () =>
                audioFileInputRef.current && audioFileInputRef.current.click()
              }
            >
              <FiUploadCloud
                size={ 44 }
                style={ { color: "#cbd5e1", marginBottom: 16 } }
              />
              <Typography color="#94a3b8" fontSize={ 14 } mb={ 2 } align="center" lineHeight={ 1.5 }>
                Drag and drop MP3, M4A, WAV or MP4 file here, or select files to
                upload.
              </Typography>
              <Button
                variant="outlined"
                style={ {
                  borderRadius: 8,
                  fontWeight: 600,
                  color: "#396afc",
                  borderColor: "#396afc",
                  background: "#fff",
                  textTransform: "none",
                  padding: "6px 20px",
                } }
                onClick={ (e) => {
                  e.stopPropagation();
                  audioFileInputRef.current &&
                    audioFileInputRef.current.click();
                } }
              >
                Select Audio Files
              </Button>
              <input
                ref={ audioFileInputRef }
                type="file"
                accept="audio/mp3,audio/m4a,audio/wav,video/mp4"
                multiple
                style={ { display: "none" } }
                onChange={ handleAudioFileSelect }
              />
            </Box>

            <Box mt={ 2 }>
              <Typography variant="body2" color="#64748b" fontSize={ 13 } lineHeight={ 1.7 }>
                <strong style={ { color: '#334155' } }>Supported formats:</strong> MP3, M4A, WAV, MP4
                <br />
                <strong style={ { color: '#334155' } }>Max file size:</strong> 200MB
              </Typography>
            </Box>

            {/* Upload Progress for Audio Files */ }
            { uploadingFiles
              .filter(
                (id) => id.type === "audioFile"
                // || !uploadedFiles.find((f) => f.id === id)
              )
              .map((fileId) => {
                const progress = uploadProgress[fileId.id] || 0;
                return (
                  <Box key={ fileId } mt={ 2 }>
                    <Box display="flex" alignItems="center" gap={ 1 } mb={ 1 }>
                      <FiMic size={ 16 } style={ { color: "#396afc" } } />
                      <Typography variant="body2" fontSize={ 12 }>
                        Uploading audio file... { Math.round(progress) }%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={ progress }
                      sx={ {
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: "#f0f0f0",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#396afc",
                        },
                      } }
                    />
                  </Box>
                );
              }) }
          </Box>

          {/* Divider */ }
          <Divider orientation="vertical" flexItem />

          {/* Right Side - Document Upload */ }
          <Box flex={ 1 }>
            <Box display="flex" alignItems="center" gap={ 2 } mb={ 3 }>
              <FiFileText size={ 24 } style={ { color: "#43cea2" } } />
              <Typography variant="h6" fontWeight={ 700 }>
                Company & Product Information
              </Typography>
            </Box>
            <Typography variant="body2" color="#64748b" mb={ 3 } lineHeight={ 1.6 }>
              Upload company documents, product information, and training
              materials to enhance AI understanding and provide better insights.
            </Typography>

            <Box
              onDragOver={ handleDocDragOver }
              onDragLeave={ handleDocDragLeave }
              onDrop={ handleDocDrop }
              sx={ {
                border: docDragActive
                  ? "2px solid #43cea2"
                  : "2px dashed #e0e7ef",
                borderRadius: 3,
                background: docDragActive ? "#f4f5fa" : "#fff",
                minHeight: 200,
                padding: "2rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "border 0.2s, background 0.2s",
                position: "relative",
              } }
              onClick={ () =>
                docFileInputRef.current && docFileInputRef.current.click()
              }
            >
              <FiFileText
                size={ 44 }
                style={ { color: "#cbd5e1", marginBottom: 16 } }
              />
              <Typography color="#94a3b8" fontSize={ 14 } mb={ 2 } align="center" lineHeight={ 1.5 }>
                Drag and drop TXT, DOC, DOCX, XLS, XLSX files here, or select
                files to upload.
              </Typography>
              <Button
                variant="outlined"
                style={ {
                  borderRadius: 8,
                  fontWeight: 600,
                  color: "#43cea2",
                  borderColor: "#43cea2",
                  background: "#fff",
                  textTransform: "none",
                  padding: "6px 20px",
                } }
                onClick={ (e) => {
                  e.stopPropagation();
                  docFileInputRef.current && docFileInputRef.current.click();
                } }
              >
                Select Documents
              </Button>
              <input
                ref={ docFileInputRef }
                type="file"
                accept=".txt,.doc,.docx,.xls,.xlsx"
                multiple
                style={ { display: "none" } }
                onChange={ handleDocFileSelect }
              />
            </Box>

            <Box mt={ 2 }>
              <Typography variant="body2" color="#64748b" fontSize={ 13 } lineHeight={ 1.7 }>
                <strong style={ { color: '#334155' } }>Supported formats:</strong> TXT, DOC, DOCX, XLS, XLSX
                <br />
                <strong style={ { color: '#334155' } }>Max file size:</strong> 50MB
              </Typography>
            </Box>

            {/* Upload Progress for Document Files */ }
            { uploadingFiles
              .filter(
                (id) => id.type === "companyDocs"
                // || !uploadedFiles.find((f) => f.id === id)
              )
              .map((fileId) => {
                const progress = uploadProgress[fileId.id] || 0;
                return (
                  <Box key={ fileId } mt={ 2 }>
                    <Box display="flex" alignItems="center" gap={ 1 } mb={ 1 }>
                      <FiFileText size={ 16 } style={ { color: "#43cea2" } } />
                      <Typography variant="body2" fontSize={ 12 }>
                        Uploading document... { Math.round(progress) }%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={ progress }
                      sx={ {
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: "#f0f0f0",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#43ce9938",
                        },
                      } }
                    />
                  </Box>
                );
              }) }
          </Box>

          {/* Divider */ }
          <Divider orientation="vertical" flexItem />

          {/* Right Side - Competitor Upload */ }
          <Box flex={ 1 }>
            <Box display="flex" alignItems="center" gap={ 2 } mb={ 3 }>
              <FiFileText size={ 24 } style={ { color: "#ff6b35" } } />
              <Typography variant="h6" fontWeight={ 700 }>
                Competitor Analysis
              </Typography>
            </Box>
            <Typography variant="body2" color="#64748b" mb={ 3 } lineHeight={ 1.6 }>
              Upload competitor documents, pricing sheets, and marketing
              materials to analyze market positioning and competitive
              advantages.
            </Typography>

            {/* Competitor Selection */ }
            <Box mb={ 3 }>
              <FormControl fullWidth size="small">
                <InputLabel>Select Competitor</InputLabel>
                <Select
                  value={ selectedCompetitor }
                  onChange={ (e) => setSelectedCompetitor(e.target.value) }
                  label="Select Competitor"
                  sx={ { borderRadius: 2 } }
                >
                  <MenuItem value="competitor1">Competitor 1</MenuItem>
                  <MenuItem value="competitor2">Competitor 2</MenuItem>
                  <MenuItem value="competitor3">Competitor 3</MenuItem>
                  <MenuItem value="competitor4">Competitor 4</MenuItem>
                  <MenuItem value="competitor5">Competitor 5</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box
              onDragOver={ handleCompetitorDragOver }
              onDragLeave={ handleCompetitorDragLeave }
              onDrop={ handleCompetitorDrop }
              sx={ {
                border: competitorDragActive
                  ? "2px solid #ff6b35"
                  : "2px dashed #e0e7ef",
                borderRadius: 3,
                background: competitorDragActive ? "#f4f5fa" : "#fff",
                minHeight: 200,
                padding: "2rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                transition: "border 0.2s, background 0.2s",
                position: "relative",
              } }
              onClick={ () =>
                competitorFileInputRef.current &&
                competitorFileInputRef.current.click()
              }
            >
              <FiFileText
                size={ 44 }
                style={ { color: "#cbd5e1", marginBottom: 16 } }
              />
              <Typography color="#94a3b8" fontSize={ 14 } mb={ 2 } align="center" lineHeight={ 1.5 }>
                Drag and drop PDF, DOC, DOCX, XLS, XLSX files here, or select
                files to upload.
              </Typography>
              <Button
                variant="outlined"
                style={ {
                  borderRadius: 8,
                  fontWeight: 600,
                  color: "#ff6b35",
                  borderColor: "#ff6b35",
                  background: "#fff",
                  textTransform: "none",
                  padding: "6px 20px",
                } }
                onClick={ (e) => {
                  e.stopPropagation();
                  competitorFileInputRef.current &&
                    competitorFileInputRef.current.click();
                } }
              >
                Select Competitor Files
              </Button>
              <input
                ref={ competitorFileInputRef }
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx"
                multiple
                style={ { display: "none" } }
                onChange={ handleCompetitorFileSelect }
              />
            </Box>

            <Box mt={ 2 }>
              <Typography variant="body2" color="#64748b" fontSize={ 13 } lineHeight={ 1.7 }>
                <strong style={ { color: '#334155' } }>Supported formats:</strong> PDF, DOC, DOCX, XLS, XLSX
                <br />
                <strong style={ { color: '#334155' } }>Max file size:</strong> 50MB
              </Typography>
            </Box>

            {/* Upload Progress for Competitor Files */ }
            { uploadingFiles
              .filter(
                (id) => id.type === selectedCompetitor
                // ||
                // !uploadedFiles.find((f) => f.id === id)
              )
              .map((fileId) => {
                const progress = uploadProgress[fileId?.id] || 0;
                return (
                  <Box key={ fileId } mt={ 2 }>
                    <Box display="flex" alignItems="center" gap={ 1 } mb={ 1 }>
                      <FiFileText size={ 16 } style={ { color: "#ff6b35" } } />
                      <Typography variant="body2" fontSize={ 12 }>
                        Uploading competitor file... { Math.round(progress) }%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={ progress }
                      sx={ {
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: "#f0f0f0",
                        "& .MuiLinearProgress-bar": {
                          backgroundColor: "#ff6b35",
                        },
                      } }
                    />
                  </Box>
                );
              }) }
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={ activeTab } index={ 1 }>
        <FileManager />
      </TabPanel>

      {/* Snackbar for notifications */ }
      <Snackbar
        open={ snackbar.open }
        autoHideDuration={ 6000 }
        onClose={ closeSnackbar }
        anchorOrigin={ { vertical: "bottom", horizontal: "right" } }
      >
        <Alert
          onClose={ closeSnackbar }
          severity={ snackbar.severity }
          sx={ { width: "100%" } }
        >
          { snackbar.message }
        </Alert>
      </Snackbar>
    </div>
  );
}
